import {
  Box,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { OrganizationVaProfile, VaCollabPreference } from "schemas/dashboard";

import StyledSwitch from "components/Styled/Switch";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import PreferenceSection from "./Section";

interface Props {
  setIsEdited?: Dispatch<SetStateAction<boolean>>;
}

const BrandPreferencesSettings = ({ setIsEdited }: Props) => {
  const [openToAll, setOpenToAll] = useState(true);
  const { currentOrg, setOrganizationVaProfile } = useContext(
    OrganizationUserContext,
  );
  const handleSelect = (
    key: keyof OrganizationVaProfile,
    value: VaCollabPreference,
  ) => {
    if (setIsEdited) setIsEdited(true);
    localStorage.setItem(`has-modified-${currentOrg?.id}`, "true");
    setOrganizationVaProfile((prev) => {
      const copy = makeDeepCopy(prev);
      if (prev && key in prev) {
        copy[key] = value;
      }
      return copy;
    });
  };

  const resetToDefault = () => {
    if (setIsEdited) setIsEdited(true);
    localStorage.setItem(`has-modified-${currentOrg?.id}`, "false");
    setOrganizationVaProfile((prev) => {
      const copy = makeDeepCopy(prev);
      copy.openToAffiliates = VaCollabPreference.some;
      copy.openToGifted = VaCollabPreference.some;
      copy.openToForms = VaCollabPreference.some;
      copy.openToSocial = VaCollabPreference.some;
      return copy;
    });
  };

  const hasModified = localStorage.getItem(`has-modified-${currentOrg?.id}`);
  useEffect(() => {
    if (hasModified === "true" && openToAll) {
      setOpenToAll(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasModified]);

  return (
    <>
      <Grid
        container
        alignItems="center"
        flexDirection={"column"}
        justifyContent="center"
      >
        <FormGroup sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <StyledSwitch
                checked={openToAll}
                onChange={(e) => {
                  trackEvent("VA Onboarding -  Open to All Brands Toggled", {
                    "Open To All": e.target.checked,
                  });
                  setOpenToAll((prev) => {
                    if (prev === true) {
                      return false;
                    }
                    resetToDefault();
                    return true;
                  });
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: { xs: 14, md: 18 }, ml: 1 }}>
                Open to all partnership types
              </Typography>
            }
          />
        </FormGroup>

        {!openToAll ? (
          <Typography
            textAlign="center"
            sx={{ fontSize: { xs: 12, md: 16 }, maxWidth: 800 }}
          >
            💡 Note: these preferences help your AI Assistant understand what
            types of brand collaborations you are open to. We'll do our best to
            match your selections, but not all opportunities will perfectly
            align - especially if brand data is limited.
          </Typography>
        ) : (
          <Typography textAlign="center" sx={{ fontSize: { xs: 12, md: 16 } }}>
            💡 When enabled, your assistant can connect with a broader set of
            brands - <br />
            expanding your reach and potential for new opportunities.
          </Typography>
        )}
      </Grid>

      {!openToAll && (
        <Box sx={{ mt: 3 }}>
          <PreferenceSection
            index={1}
            title="Affiliate Partnerships"
            question="How open are you to affiliate partnerships?"
            description={
              <>
                Affiliate partnerships usually pay you a commission on sales
                made through a unique link.
                <br />
                They can be a flexible way to earn, but not every creator
                prefers them.
              </>
            }
            handleSelect={handleSelect}
            questionKey="openToAffiliates"
            labels={{
              [VaCollabPreference.all]: "Include all affiliate partnerships",
              [VaCollabPreference.none]: "I don't prefer these brands",
              [VaCollabPreference.some]: "Open to some affiliate programs",
            }}
          />
          <PreferenceSection
            index={2}
            title="Gifted / Barter Partnerships"
            question="How open are you to gifted or barter partnerships?"
            description={
              <>
                Gifted partnerships (also called barter deals) mean you receive
                products or services instead of cash payments.
              </>
            }
            handleSelect={handleSelect}
            questionKey="openToGifted"
            labels={{
              [VaCollabPreference.all]: "Include all gifted partnerships",
              [VaCollabPreference.some]: "Open to some gifted deals",
              [VaCollabPreference.none]: "I don't prefer these brands",
            }}
          />
          <PreferenceSection
            index={3}
            title="Brands That Require a Form"
            question="How open are you to brands that require an application form?"
            description={
              <>
                Some brands ask creators to complete short application forms to
                collect details like content type or rates before a
                collaboration.
              </>
            }
            handleSelect={handleSelect}
            questionKey="openToForms"
            labels={{
              [VaCollabPreference.all]: "No restriction",
              [VaCollabPreference.some]: "Open to some",
              [VaCollabPreference.none]: "Avoid these brands",
            }}
          />
          <PreferenceSection
            index={4}
            title="Brands That Request Social Links"
            question="How open are you to  brands that ask for your social media profiles?"
            description={
              <>
                Many brands want to review your social media profiles before
                collaborating. Providing links can help them assess fit more
                quickly.
              </>
            }
            handleSelect={handleSelect}
            questionKey="openToSocial"
            labels={{
              [VaCollabPreference.all]: "No restriction",
              [VaCollabPreference.some]: "Open to some",
              [VaCollabPreference.none]: "Avoid these brands",
            }}
          />
        </Box>
      )}
    </>
  );
};

export default BrandPreferencesSettings;
